import React, { useState } from "react";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getRestaurantById,
  updateManagerChatAccess,
  updateRestaurantPlatformFee,
  updateRestaurantStatus,
} from "../../../../api/admin/restaurantManagement";
import toast from "react-hot-toast";
import ImagesSlider from "./RestaurantImagesSlider";
import DocsSlider from "./DocsSlider";

import PRofileID from "../../../../helper/svg/ID.svg";
import Smartphone from "../../../../helper/svg/smartphone.svg";
import Avtar from "../../../../helper/images/Avtar.png";
import { TbMailFilled } from "react-icons/tb";
import { FaLocationDot } from "react-icons/fa6";
import { IoStarSharp } from "react-icons/io5";
import Userdoc from "../../../../helper/images/userdoc.png";
import { FaEdit } from "react-icons/fa";
import { MdStar, MdStarHalf, MdStarOutline } from "react-icons/md";
import { useTranslation } from "../../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const RestaurantDetailPage = () => {
  const { t } = useTranslation();
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [platformFee, setPlatformFee] = useState("");
  const [error, setError] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["restaurant", restaurantId],
    queryFn: () => getRestaurantById(restaurantId),
    enabled: !!restaurantId,
  });

  // const chatAccessMutation = useMutation({
  //   mutationFn: (access) =>
  //     updateManagerChatAccess(restaurantId, { managerChatAccess: access }),
  //   onSuccess: () => {
  //     toast.success("Manager chat access updated");
  //     queryClient.invalidateQueries(["restaurant", restaurantId]);
  //   },
  //   onError: () => toast.error("Failed to update chat access"),
  // });

  const chatAccessMutation = useMutation({
    mutationFn: (data) => updateManagerChatAccess(data),
    onSuccess: () => {
      toast.success(t("restaurantManagement").messages.updateSuccess);
      queryClient.invalidateQueries(["restaurant", restaurantId]);
    },
    onError: () => {
      toast.error(t("restaurantManagement").messages.updateFailed);
    },
  });

  const statusMutation = useMutation({
    mutationFn: (payload) => updateRestaurantStatus(restaurantId, payload),
    onSuccess: () => {
      toast.success("Restaurant status updated");
      queryClient.invalidateQueries(["restaurant", restaurantId]);
    },
    onError: () => toast.error("Failed to update status"),
  });

  const platformFeeMutation = useMutation({
    mutationFn: (payload) => updateRestaurantPlatformFee(restaurantId, payload),
    onSuccess: () => {
      toast.success(t("restaurantManagement").platformFeeSuccess);
      queryClient.invalidateQueries(["restaurant", restaurantId]);
      setIsPopupOpen(false);
    },
    onError: () => toast.error("Failed to update status"),
  });

  const restaurant = data?.data;

  const handleApprove = () => {
    statusMutation.mutate({
      verificationStatus: "approved",
      rejectionReason: "",
    });
  };

  const handleReject = () => {
    if (!rejectionReason) return toast.error("Please enter a rejection reason");
    statusMutation.mutate({
      verificationStatus: "rejected",
      rejectionReason,
    });
  };

  const handleChange = (e) => {
    if (e.target.value > 100) {
      setError(t("restaurantManagement").invalidPercentage);
    } else {
      setError("");
    }
    setPlatformFee(e.target.value);
  };

  const onUpdatePlatformFee = () => {
    // Check if input is empty
    if (!platformFee || platformFee.toString().trim() === "") {
      setError(t("restaurantManagement").platformFeeEmptyError || "Platform fee cannot be empty");
      return;
    }
    
    // Check if value is greater than 100
    if (platformFee > 100) {
      setError(t("restaurantManagement").invalidPercentage);
      return;
    }
    
    // Clear any previous errors
    setError("");
    platformFeeMutation.mutate({ platformFee });
  };

  const openModal = () => {
    setPlatformFee(restaurant?.platformFee || "");
    setError("");
    setIsPopupOpen(true);
  };

  const closeModal = () => {
    setIsPopupOpen(false);
    setError("");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const handleToggleChatAccess = (restaurantId, currentValue, status) => {
    if (status === "rejected") {
      return false;
    }

    chatAccessMutation.mutate({
      restaurantId,
      managerChatAccess: !currentValue,
    });
  };

  return (
    <div>
      <div className="flex gap-[10px] items-center">
        <div
          onClick={() => navigate(-1)}
          className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00] cursor-pointer"
        >
          <IoIosArrowRoundBack size={24} className="text-[#fff]" />
        </div>
        <h2 className="text-[22px] font-medium">
          {t("restaurantManagement").restairantTitle}
        </h2>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-[30px] mt-[30px]">
        <div className="border border-[#DDDDDD] rounded-[10px] bg-[#fff] p-[15px] lg:p-[25px] 2xl:p-[38px]">
          <ImagesSlider images={restaurant.images || []} />
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-[30px] mt-[16px] pb-[23px] border-b border-[#D9D9D9] items-center">
            <div>
              <h2 className="text-[30px] font-semibold mb-[10px]">
                {restaurant.name}
              </h2>
              <p className="text-lg mb-[18px]">{restaurant.address}</p>
              <div className="flex items-center gap-[29px]">
                <p className="text-lg font-medium">
                  {restaurant.rating || 0} / 5
                </p>
                <div className="flex items-center">
                  {Array(Math.floor(restaurant?.rating))
                    .fill()
                    .map((_, index) => (
                      <MdStar
                        key={`full-${index}`}
                        size={21}
                        className="text-[#FF9D00]"
                      />
                    ))}

                  {restaurant?.rating % 1 !== 0 && (
                    <MdStarHalf size={21} className="text-[#FF9D00]" />
                  )}

                  {Array(5 - Math.ceil(restaurant?.rating))
                    .fill()
                    .map((_, index) => (
                      <MdStarOutline
                        key={`empty-${index}`}
                        size={21}
                        className="text-[#FF9D00]"
                      />
                    ))}
                </div>
              </div>

              {restaurant?.verificationStatus === "approved" && (
                <div className="flex items-center justify-between mb-[16px]">
                  <h3 className="text-[18px] font-medium leading-[22px]">
                    <span>{t("restaurantManagement").platformFee}</span>
                  </h3>
                  <div className="flex items-center">
                    <p className="text-[16px] text-gray-800 mr-3">
                      {restaurant?.platformFee}%
                    </p>
                    <button
                      onClick={openModal}
                      className="text-blue-500 hover:text-blue-700 focus:outline-none"
                    >
                      <FaEdit className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="2xl:text-right  2xl:justify-end">
              <div className="flex gap-[22px] items-center justify-end">
                <h3 className="text-lg font-medium">
                  {t("restaurantManagement").managerChatAccess}
                </h3>
                <div
                  // onClick={() =>
                  //   chatAccessMutation.mutate(!restaurant.managerChatAccess)
                  // }
                  onClick={() =>
                            handleToggleChatAccess(
                              restaurant._id,
                              restaurant.managerChatAccess,
                              restaurant.verificationStatus
                            )
                          }
                  className={`w-[48px] h-[20px] flex items-center rounded-full p-1 cursor-pointer transition-colors ${
                    restaurant.managerChatAccess
                      ? "bg-[#4CAF50]"
                      : "bg-[#e1e1e1]"
                  }`}
                >
                  <div
                    className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                      restaurant.managerChatAccess
                        ? "translate-x-6"
                        : "translate-x-0"
                    }`}
                  />
                </div>
              </div>
              <p className="text-sm text-[#747474] mt-[14px]">
                {t("restaurantManagement").managerChatAccessInfo}
              </p>
            </div>
          </div>

          <div className="p-[20px] border border-[#D9D9D9] rounded-[10px] bg-[#fff] mt-[21px]">
            <h3 className="text-lg font-medium">
              {t("restaurantManagement").userDocuments}
            </h3>
            <div className="flex gap-[14px] flex-wrap mt-[21px]">
              {/* {restaurant.verificationDocs?.map((doc, i) => (
                <img key={i} src={`${doc}`} alt="doc" className="w-[120px] h-auto" />
              ))} */}

              <DocsSlider images={restaurant.verificationDocs || []} />
            </div>
          </div>
        </div>

        <div>
          <div className="w-full xl:max-w-[360px] border border-[#DDDDDD] rounded-[12px] bg-white p-2">
            <div className="bg-[#FFFAF2] h-[132px] rounded-[3.56px]"></div>
            <img
              src={
                restaurant.ownerId?.profilePicture
                  ? `${restaurant.ownerId.profilePicture}`
                  : Avtar
              }
              alt="avatar"
              className="w-full max-w-[159px] h-[159px] mx-auto rounded-full border-4 border-white -mt-[80px]"
            />
            <h2 className="text-xl font-medium text-center mt-5">
              {restaurant.ownerId?.name || "N/A"}
            </h2>
            <div className="bg-[#E1EDDE] px-2 py-1 text-center text-[#1B7200] rounded w-max mx-auto mt-[7px] mb-5">
              {t("restaurantManagement").owner}
            </div>

            <div className="py-5 px-7 border-t border-[#E0E2E7]">
              <ul className="space-y-4">
                <li className="flex gap-[14px]">
                  <span className="bg-[#F0F1F3] rounded-full w-[36px] h-[36px] flex items-center justify-center">
                    <img src={PRofileID} alt="" />
                  </span>
                  <div>
                    <h5 className="text-[#667085] text-base">
                      {t("userprofile").userId}
                    </h5>
                    <p className="text-[#353535] text-base">
                      {restaurant.ownerId?._id}
                    </p>
                  </div>
                </li>
                <li className="flex gap-[14px]">
                  <span className="bg-[#F0F1F3] rounded-full w-[36px] h-[36px] flex items-center justify-center">
                    <TbMailFilled />
                  </span>
                  <div>
                    <h5 className="text-[#667085] text-base">
                      {t("userprofile").email}
                    </h5>
                    <p className="text-[#353535] text-base">
                      {restaurant.ownerId?.email}
                    </p>
                  </div>
                </li>
                <li className="flex gap-[14px]">
                  <span className="bg-[#F0F1F3] rounded-full w-[36px] h-[36px] flex items-center justify-center">
                    <FaLocationDot />
                  </span>
                  <div>
                    <h5 className="text-[#667085] text-base">
                      {t("userprofile").address}
                    </h5>
                    <p className="text-[#353535] text-base">
                      {restaurant.ownerId?.address || "N/A"}
                    </p>
                  </div>
                </li>
                <li className="flex gap-[14px]">
                  <span className="bg-[#F0F1F3] rounded-full w-[36px] h-[36px] flex items-center justify-center">
                    <img src={Smartphone} alt="" />
                  </span>
                  <div>
                    <h5 className="text-[#667085] text-base">
                      {t("userprofile").phoneNumber}
                    </h5>
                    <p className="text-[#353535] text-base">
                      {restaurant.ownerId?.phoneNumber}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {restaurant?.verificationStatus !== "approved" && (
            <>
              {restaurant?.verificationStatus === "rejected" ? (
                <div className="mt-[37px] w-full rounded border border-[#DDDDDD] p-[16px] bg-[#FFFAF2]">
                  <p className="text-[#18181B]">
                    {restaurant?.rejectionReason ||
                      "No rejection reason provided."}
                  </p>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleApprove}
                    className="mt-[37px] w-full rounded bg-[#4CAF50] text-white text-center font-bold py-3 hover:bg-[#209b25]"
                  >
                    {t("restaurantManagement").approveRestaurant}
                  </button>

                  <textarea
                    placeholder="Provide a reason for rejection"
                    className="w-full h-[154px] mt-[20px] rounded border border-[#DDDDDD] p-[16px] text-[#18181B] placeholder:text-[#BEBEBE]"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  ></textarea>

                  <button
                    onClick={handleReject}
                    className="mt-[18px] w-full rounded bg-[#FF0000] text-white text-center font-bold py-3 hover:bg-[#D62323]"
                  >
                    {t("restaurantManagement").rejectRestaurant}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={closeModal}>
        <div className="p-[30px]">
          <div className="pb-[21px] border-b border-[#EEEEEE] mb-[19px]">
            <h2 className="text-[22px] font-medium">
              {t("restaurantManagement").updatePlatformFeeTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm mb-[8px] block">
                {t("restaurantManagement").platformFee} (%)
              </label>
              <div className="relative w-full">
                <input
                  type="number"
                  value={platformFee}
                  onChange={handleChange}
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                    error ? "border-red-500" : "focus:border-[#4CAF50]"
                  }`}
                  placeholder={t("restaurantManagement").platformFeePlaceholder}
                  min="0"
                  max="100"
                />
                {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
              </div>
            </div>

            <div className="flex gap-[30px] justify-end pt-[23px]">
              <button
                onClick={closeModal}
                className="text-white py-[14px] px-[31px] rounded-[2px] bg-[#CCCCCC] hover:bg-[#B8B8B8] text-[20px] font-bold"
              >
                {t("restaurantManagement").cancel}
              </button>
              <button
                onClick={onUpdatePlatformFee}
                className="bg-[#4CAF50] text-white py-[14px] px-[30px] rounded-[2px] text-[20px] font-bold hover:bg-[#209b25]"
              >
                {t("restaurantManagement").update}
              </button>
            </div>
          </div>
        </div>
      </PopupModal>
    </div>
  );
};

export default RestaurantDetailPage;
